import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { categories } from '@/mocks/categories';

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileCatOpen, setMobileCatOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id: string) => {
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth' });
    }
    setMobileOpen(false);
  };

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? 'bg-white/95 backdrop-blur-md shadow-sm'
          : 'bg-transparent'
      }`}
    >
      <div className="w-full px-6 md:px-10">
        <div className="flex items-center justify-between h-16">
          <Link
            to="/"
            className="font-heading text-xl md:text-2xl font-bold text-foreground-950 tracking-tight whitespace-nowrap"
          >
            SuperBuy <span className="font-sans font-light text-foreground-500">Spread</span>
          </Link>

          <div className="hidden md:flex items-center gap-8">
            <button
              onClick={() => scrollToSection('categories')}
              className="font-label text-sm text-foreground-600 hover:text-foreground-950 transition-colors duration-200 cursor-pointer whitespace-nowrap"
            >
              Categories
            </button>
            <button
              onClick={() => scrollToSection('about')}
              className="font-label text-sm text-foreground-600 hover:text-foreground-950 transition-colors duration-200 cursor-pointer whitespace-nowrap"
            >
              Guide
            </button>
            <Link
              to="/guide/superbuy-spreadsheet-guide"
              className="font-label text-sm text-foreground-600 hover:text-foreground-950 transition-colors duration-200 cursor-pointer whitespace-nowrap"
            >
              Articles
            </Link>
            <button
              onClick={() => scrollToSection('safety')}
              className="font-label text-sm text-foreground-600 hover:text-foreground-950 transition-colors duration-200 cursor-pointer whitespace-nowrap"
            >
              Safety
            </button>
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="btn-primary text-xs px-6 py-2.5"
            >
              Buy It Now
              <i className="ri-arrow-right-line ml-2"></i>
            </a>
          </div>

          <button
            className="md:hidden w-10 h-10 flex items-center justify-center text-foreground-800 cursor-pointer"
            onClick={() => setMobileOpen(!mobileOpen)}
          >
            <i className={`text-xl ${mobileOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div className="md:hidden bg-white/98 backdrop-blur-md border-t border-background-200 animate-fade-in overflow-y-auto max-h-[80vh]">
          <div className="flex flex-col px-6 py-4">
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              onClick={() => setMobileOpen(false)}
              className="btn-primary text-center mb-4"
            >
              Buy It Now
              <i className="ri-arrow-right-line ml-2"></i>
            </a>

            <div className="border-t border-background-100 pt-4 mb-2">
              <button
                onClick={() => setMobileCatOpen(!mobileCatOpen)}
                className="w-full flex items-center justify-between font-label text-sm font-semibold text-foreground-800 py-3 cursor-pointer"
              >
                <span>Browse Categories</span>
                <i className={`ri-arrow-down-s-line text-foreground-400 transition-transform duration-200 ${mobileCatOpen ? 'rotate-180' : ''}`}></i>
              </button>
              {mobileCatOpen && (
                <div className="flex flex-wrap gap-2 pb-3 animate-fade-in">
                  {categories.map((cat) => (
                    <a
                      key={cat.name}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      onClick={() => setMobileOpen(false)}
                      className="bg-background-100 border border-background-200 text-foreground-600 font-body text-xs px-3 py-1.5 rounded-full cursor-pointer hover:bg-foreground-900 hover:text-white hover:border-foreground-900 transition-all duration-200 whitespace-nowrap"
                    >
                      {cat.name}
                    </a>
                  ))}
                </div>
              )}
            </div>

            <div className="border-t border-background-100 pt-4">
              <p className="font-label text-xs text-foreground-400 uppercase tracking-wider mb-2">Guides &amp; Articles</p>
              <Link
                to="/guide/superbuy-spreadsheet-guide"
                onClick={() => setMobileOpen(false)}
                className="flex items-center gap-2 font-body text-sm text-foreground-700 hover:text-foreground-950 py-2.5 cursor-pointer whitespace-nowrap"
              >
                <i className="ri-article-line text-foreground-400"></i>
                Complete Guide 2026
              </Link>
              <Link
                to="/guide/superbuy-spreadsheet-beginners"
                onClick={() => setMobileOpen(false)}
                className="flex items-center gap-2 font-body text-sm text-foreground-700 hover:text-foreground-950 py-2.5 cursor-pointer whitespace-nowrap"
              >
                <i className="ri-user-star-line text-foreground-400"></i>
                Beginner&apos;s Handbook
              </Link>
              <Link
                to="/guide/superbuy-spreadsheet-sneakers"
                onClick={() => setMobileOpen(false)}
                className="flex items-center gap-2 font-body text-sm text-foreground-700 hover:text-foreground-950 py-2.5 cursor-pointer whitespace-nowrap"
              >
                <i className="ri-footprint-line text-foreground-400"></i>
                Sneaker Guide
              </Link>
              <Link
                to="/guide/superbuy-spreadsheet-safety"
                onClick={() => setMobileOpen(false)}
                className="flex items-center gap-2 font-body text-sm text-foreground-700 hover:text-foreground-950 py-2.5 cursor-pointer whitespace-nowrap"
              >
                <i className="ri-shield-check-line text-foreground-400"></i>
                Safety &amp; Smart Shopping
              </Link>
            </div>

            <div className="border-t border-background-100 pt-4 mt-2">
              <p className="font-label text-xs text-foreground-400 uppercase tracking-wider mb-2">Quick Links</p>
              <button
                onClick={() => scrollToSection('categories')}
                className="flex items-center gap-2 font-body text-sm text-foreground-700 hover:text-foreground-950 py-2.5 cursor-pointer whitespace-nowrap w-full text-left"
              >
                <i className="ri-grid-line text-foreground-400"></i>
                Browse Categories
              </button>
              <button
                onClick={() => scrollToSection('about')}
                className="flex items-center gap-2 font-body text-sm text-foreground-700 hover:text-foreground-950 py-2.5 cursor-pointer whitespace-nowrap w-full text-left"
              >
                <i className="ri-information-line text-foreground-400"></i>
                About SuperBuy Spreadsheet
              </button>
              <button
                onClick={() => scrollToSection('safety')}
                className="flex items-center gap-2 font-body text-sm text-foreground-700 hover:text-foreground-950 py-2.5 cursor-pointer whitespace-nowrap w-full text-left"
              >
                <i className="ri-shield-user-line text-foreground-400"></i>
                Safety Center
              </button>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}